import { authenticatedFetch } from '../utils/apiInterceptor';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000'; 

export const getCompanyNameFromDOT = async (dotNumber) => {
    const webKey = process.env.REACT_APP_FMCSA_API_KEY;
    if (!webKey) throw new Error("Client-side API key is not configured.");

    const fmcsaApiUrl = `https://mobile.fmcsa.dot.gov/qc/services/carriers/${dotNumber}?webKey=${webKey}`;

    try {
        const response = await fetch(fmcsaApiUrl);
        if (!response.ok) throw new Error(`DOT Lookup failed. Status: ${response.status}`);
        const data = await response.json();

        // Handle FMCSA's inconsistent root structure (sometimes array, sometimes object)
        const rootContent = data?.content?.[0] || data?.content;
        if (!rootContent || !rootContent.carrier) throw new Error('Company not found for this DOT number.');

        const carrierData = rootContent.carrier;

        const city = carrierData.phyCity || '';
        const state = carrierData.phyState || '';
        const zip = carrierData.phyZipcode || '';
        const cityState = [city, state].filter(Boolean).join(', ');
        const cityStZip = [cityState, zip].filter(Boolean).join(' ');

        let fetchedMcNumber = '';

        // --- STEP 1: Try checking inside the main carrier object first ---
        if (carrierData.mcNumber) {
            fetchedMcNumber = carrierData.mcNumber.toString();
        } else if (carrierData.docketNumber) {
            fetchedMcNumber = carrierData.docketNumber.toString();
        } else if (carrierData.allowedOperatingAuthorities && carrierData.allowedOperatingAuthorities.length > 0) {
            fetchedMcNumber = carrierData.allowedOperatingAuthorities[0].docketNumber || '';
        } else if (carrierData.mcMxffNumbers && typeof carrierData.mcMxffNumbers === 'string') {
            fetchedMcNumber = carrierData.mcMxffNumbers;
        }

        // --- STEP 2: If not found, fetch from the separated docket-numbers link ---
        if (!fetchedMcNumber && rootContent?._links?.['docket numbers']?.href) {
            const docketNumbersHref = rootContent._links['docket numbers'].href;
            try {
                // Fetch the secondary link with the API key attached
                const docketResponse = await fetch(`${docketNumbersHref}?webKey=${webKey}`);
                if (docketResponse.ok) {
                    const docketData = await docketResponse.json();
                    const docketContent = docketData?.content;
                    
                    if (Array.isArray(docketContent) && docketContent.length > 0) {
                        // Find the object where the prefix is "MC"
                        const mcDocket = docketContent.find(d => d.prefix === 'MC' || d.docketType === 'MC') || docketContent[0];
                        
                        if (mcDocket) {
                            // Safely extract depending on if the government sends a string or object
                            if (typeof mcDocket === 'string') {
                                fetchedMcNumber = mcDocket;
                            } else if (mcDocket.docketNumber) {
                                fetchedMcNumber = mcDocket.docketNumber.toString();
                            }
                        }
                    }
                } else {
                    console.warn(`Failed to fetch docket numbers. Status: ${docketResponse.status}`);
                }
            } catch (docketError) {
                console.warn('Could not fetch isolated docket number, skipping:', docketError);
            }
        }

        // --- STEP 3: Clean the final string ---
        // Strip out any text/formatting so only the raw digits are left (e.g. "1698382")
        // Your AuthForm.js will automatically add the "MC-" prefix when setting state
        if (fetchedMcNumber) {
            fetchedMcNumber = fetchedMcNumber.replace(/\D/g, '');
        }

        return { 
            companyName: carrierData.legalName || carrierData.dbaName || '', 
            legalName: carrierData.legalName || '', 
            dbaName: carrierData.dbaName || '', 
            street: carrierData.phyStreet || '', 
            cityStZip: cityStZip, 
            powerUnits: carrierData.totalPowerUnits || '',
            dot_number: carrierData.dotNumber || dotNumber, // Use DOT from response if available
            mcNumber: fetchedMcNumber 
        };
    } catch (error) {
        console.error('FMCSA DOT API call failed:', error);
        throw error;
    }
};

export const getCompanyNameFromMC = async (mcNumber) => {
    const webKey = process.env.REACT_APP_FMCSA_API_KEY;
    if (!webKey) throw new Error("Client-side API key is not configured.");

    const cleanMC = mcNumber.replace(/\D/g, ''); 
    const fmcsaApiUrl = `https://mobile.fmcsa.dot.gov/qc/services/carriers/docket-number/${cleanMC}?webKey=${webKey}`;

    try {
        const response = await fetch(fmcsaApiUrl);
        if (!response.ok) throw new Error(`MC Lookup failed. Status: ${response.status}`);
        
        const rawData = await response.json();

        if (!rawData.content || rawData.content.length === 0) {
            throw new Error('Company not found for this MC number.');
        }

        const carrierData = rawData.content[0].carrier || rawData.content[0];

        const city = carrierData.phyCity || '';
        const state = carrierData.phyState || '';
        const zip = carrierData.phyZipcode || '';
        const cityState = [city, state].filter(Boolean).join(', ');
        const cityStZip = [cityState, zip].filter(Boolean).join(' ');

        return { 
            companyName: carrierData.legalName || carrierData.dbaName || '', 
            legalName: carrierData.legalName || '', 
            dbaName: carrierData.dbaName || '', 
            street: carrierData.phyStreet || '', 
            cityStZip: cityStZip, 
            powerUnits: carrierData.totalPowerUnits || '',
            dot_number: carrierData.dotNumber || ''
        };
    } catch (error) {
        console.error('FMCSA MC API Error:', error);
        throw error;
    }
};

/**
 * Sends sign-up data to the backend for processing.
 */
export const signupUser = async (role, formData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/auth/signup/${role}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ role, ...formData }),
        });

        const contentType = response.headers.get('content-type') || '';
        const responseText = await response.text();

        if (!response.ok) {
            let errorText = 'Sign up failed.';
            if (contentType.includes('application/json')) {
                try {
                    const parsed = JSON.parse(responseText);
                    errorText = parsed.message || errorText;
                } catch {
                    errorText = responseText || errorText;
                }
            } else errorText = responseText || errorText;
            throw new Error(errorText);
        }

        let data;
        if (contentType.includes('application/json')) {
            try {
                data = JSON.parse(responseText);
            } catch {
                data = { message: responseText };
            }
        } else {
            data = { message: responseText };
        }
        console.log('Sign up successful:', data);
        return data;
    } catch (error) {
        console.error('Sign up API call failed:', error);
        throw new Error(error.message || 'An error occurred during sign up.');
    }
};

/**
 * Sends sign-in data to the backend for verification.
 * Now handles session conflict responses (status 409)
 */
export const signinUser = async (role, formData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/auth/signin/${role}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
        });

        const contentType = response.headers.get('content-type') || '';
        const responseText = await response.text();
        if (response.status === 409) {
            let data;
            try {
                data = JSON.parse(responseText);
            } catch {
                data = { 
                    requiresConfirmation: true,
                    message: 'You are already signed in on another device or browser.'
                };
            }
            return data;
        }

        if (!response.ok) {
            let errorText = 'Sign-in failed.';
            if (contentType.includes('application/json')) {
                try {
                    const parsed = JSON.parse(responseText);
                    errorText = parsed.message || errorText;
                } catch {
                    errorText = responseText || errorText;
                }
            } else errorText = responseText || errorText;
            throw new Error(errorText);
        }

        let data;
        if (contentType.includes('application/json')) {
            try {
                data = JSON.parse(responseText);
            } catch {
                data = { message: responseText };
            }
        } else {
            data = { message: responseText };
        }
        return data;
    } catch (error) {
        console.error('Sign-in API call failed:', error);
        throw new Error(error.message || 'An error occurred during sign-in.');
    }
};

/**
 * Checks if a given email already exists in the backend database.
 */
export const checkEmailExists = async (email, role) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/auth/check-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({email, role}),
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error checking email:', error);
        return { exists: false };
    }
};

/**
 * Fetches the current user's profile details from backend.
 */
export const getUserProfile = async (role, userId) => {
    try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/${role}s/${userId}`);
        if (!response.ok) throw new Error('Failed to fetch user profile');
        return await response.json();
    } catch (error) {
        console.error('Error fetching user profile:', error);
        throw error;
    }
};

/**
 * Updates user profile information in backend.
 */
export const updateUserProfile = async (formData, token) => {
    try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/auth/update-profile`, {
            method: 'PUT',
            body: JSON.stringify(formData),
        });

        const contentType = response.headers.get('content-type') || '';
        const responseText = await response.text();

        if (!response.ok) {
            let errorMessage = 'Failed to update user profile';
            if (contentType.includes('application/json')) {
                try {
                    const parsed = JSON.parse(responseText);
                    errorMessage = parsed.message || errorMessage;
                } catch (err) {
                    errorMessage = responseText || errorMessage;
                }
            } else {
                errorMessage = responseText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        let data;
        if (contentType.includes('application/json')) {
            try {
                data = JSON.parse(responseText);
            } catch (err) {
                data = { success: true, user: formData };
            }
        } else {
            data = { success: true, user: formData };
        }

        return data;
    } catch (error) {
        console.error('Error updating user profile:', error);
        throw new Error('Failed to update user profile');
    }
};

/**
 * Sign out user and clear session token
 */
export const signoutUser = async (token) => {
    try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/auth/signout`, {
            method: 'POST',
        });

        if (!response.ok) {
            throw new Error('Failed to sign out');
        }

        const data = await response.json();
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('user');
        localStorage.removeItem('role');
        
        return data;
    } catch (error) {
        console.error('Error signing out:', error);
        throw error;
    }
};